// ===========================================
// konakla - Realtime Stats Hook
// ===========================================

"use client";

import { useEffect, useState, useCallback } from "react";
import {
  subscribeToListingViews,
  subscribeToContactClicks,
  subscribeToOwnerActivity,
} from "@/lib/supabase/realtime";

interface RealtimeStats {
  viewsToday: number;
  clicksToday: number;
  totalViews: number;
  totalClicks: number;
}

interface UseRealtimeStatsOptions {
  listingId?: string;
  ownerId?: string;
  initialStats?: Partial<RealtimeStats>;
}

interface UseRealtimeStatsReturn {
  stats: RealtimeStats;
  isConnected: boolean;
  lastUpdate: Date | null;
}

/**
 * Hook to subscribe to realtime stats for a listing or owner
 */
export function useRealtimeStats(options: UseRealtimeStatsOptions): UseRealtimeStatsReturn {
  const { listingId, ownerId, initialStats } = options;

  const [stats, setStats] = useState<RealtimeStats>({
    viewsToday: initialStats?.viewsToday ?? 0,
    clicksToday: initialStats?.clicksToday ?? 0,
    totalViews: initialStats?.totalViews ?? 0,
    totalClicks: initialStats?.totalClicks ?? 0,
  });

  // isConnected, subscription'ların var olup olmayacağına bağlı olarak türetilir.
  // Prop'lardan derive etmek effect'te setState çağırmaya göre daha güvenli.
  const isConnected = Boolean(listingId || ownerId);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Increment view count
  const incrementViews = useCallback(() => {
    setStats((prev) => ({
      ...prev,
      viewsToday: prev.viewsToday + 1,
      totalViews: prev.totalViews + 1,
    }));
    setLastUpdate(new Date());
  }, []);

  // Increment click count
  const incrementClicks = useCallback(() => {
    setStats((prev) => ({
      ...prev,
      clicksToday: prev.clicksToday + 1,
      totalClicks: prev.totalClicks + 1,
    }));
    setLastUpdate(new Date());
  }, []);

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Subscribe based on options
    if (listingId) {
      // Single listing subscriptions
      unsubscribers.push(
        subscribeToListingViews(listingId, () => {
          incrementViews();
        })
      );

      unsubscribers.push(
        subscribeToContactClicks(listingId, () => {
          incrementClicks();
        })
      );
    } else if (ownerId) {
      // Owner-wide subscriptions
      unsubscribers.push(
        subscribeToOwnerActivity(ownerId, {
          onView: () => incrementViews(),
          onClick: () => incrementClicks(),
        })
      );
    }

    // Cleanup on unmount
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [listingId, ownerId, incrementViews, incrementClicks]);

  return { stats, isConnected, lastUpdate };
}

/**
 * Hook to subscribe to pending listings count (admin)
 */
export function usePendingListingsCount(initialCount = 0): {
  count: number;
  isConnected: boolean;
} {
  const [count, setCount] = useState(initialCount);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    // Import dinamik olarak (sadece admin için)
    import("@/lib/supabase/realtime").then(({ subscribeToPendingListings }) => {
      if (cancelled) return;
      unsubscribe = subscribeToPendingListings(() => {
        setCount((prev) => prev + 1);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  // Bu hook her zaman mount sonrası bağlanmaya çalışır; UI'da kullanılıyorsa
  // true döndürmek pratik olarak yeterli — gerçek connection state gerekirse
  // realtime kanalı status listener'ına geçilebilir.
  return { count, isConnected: true };
}

export default useRealtimeStats;
